// App.jsx
import React, { useEffect, useState, Suspense } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { auth } from "./common/utils/firebase";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RefundPolicy from './landing_page/pages/RefundPolicy';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import TawkToWidget from './common/components/TawkToWidget';
import CommonLoadingSpinner from "./common/components/CommonLoadingSpinner";

// Lazy load components for better performance
const InterviewSession = React.lazy(() => import('./interview/pages/InterviewSession'));
const ForgotPassword = React.lazy(() => import('./login/pages/ForgotPassword'));
const Profile = React.lazy(() => import("./profile/pages/Profile"));
const UnifiedAuth = React.lazy(() => import('./login/pages/UnifiedAuth'));
const LandingPage = React.lazy(() => import('./landing_page/pages/LandingPage'));
const Pricing = React.lazy(() => import('./landing_page/pages/Pricing'));
const ProfilePricing = React.lazy(() => import('./profile/pages/ProfilePricing'));
const ChatBubble = React.lazy(() => import('./common/ChatBubble'));
const PrivacyPolicy = React.lazy(() => import('./landing_page/pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./landing_page/pages/TermsOfService'));
const Blog = React.lazy(() => import('./landing_page/pages/Blog'));
const VisaBlueprint = React.lazy(() => import('./visa-blueprint/pages/VisaBlueprintPage'));
const VisaPrepPlanPage = React.lazy(() => import('./personalized_plan/pages/VisaPrepPlanPage'));
const RedflagIdentifier = React.lazy(() => import('./Redflag_indicator/pages/redflag_indicator'));
const Visaconsularchat = React.lazy(()=>import('./VisaConsular_AI/pages/AI_consularchat'));
const VisaDiscoveryTool = React.lazy(()=>import('./visa_discovery_tool/pages/visa_discover'))



// Loading component
const LoadingFallback = () => (
  <CommonLoadingSpinner />
);

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userGeoData, setUserGeoData] = useState(null);

  // Function to update Sentry user context
  const updateSentryContext = (userData, geoData) => {
    if (userData) {
      Sentry.setUser({
        id: userData.uid,
        email: userData.email,
        username: userData.displayName || userData.email?.split('@')[0] || 'Anonymous User',
        ...geoData && {
          country: geoData.country,
          region: geoData.region,
          city: geoData.city
        }
      });
      
      Sentry.setTag("user_created_at", userData.metadata?.creationTime || "unknown");
      if (geoData) {
        Sentry.setTag("user_country", geoData.country);
        Sentry.setTag("user_region", geoData.region);
        Sentry.setTag("user_city", geoData.city);
      }
    } else {
      Sentry.setUser(null);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      setIsLoading(false);
      updateSentryContext(user, userGeoData);
    });
    
    return () => unsubscribe();
  }, [userGeoData]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  return (
    <HelmetProvider>
      <Router>
        <div className="App">
          <TawkToWidget user={user} />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public routes - no auth check */}
              <Route path="/" element={<LandingPage onGeoDataUpdate={setUserGeoData} />} />
              <Route path="/login" element={<UnifiedAuth />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />

              {/* Protected routes - require authentication */}
              <Route
                path="/profile"
                element={
                  user ? <Profile /> : <Navigate to="/login" state={{ from: "/profile" }} />
                }
              />
              <Route
                path="/profile/pricing"
                element={
                  user ? <ProfilePricing userDetails={user} onLogout={() => auth.signOut()} /> : <Navigate to="/login" state={{ from: "/profile/pricing" }} />
                }
              />
              <Route
                path="/InterviewSession"
                element={
                  user ? <InterviewSession /> : <Navigate to="/login" state={{ from: "/InterviewSession" }} />
                }
              />
              <Route
                path="/visa-blueprint"
                element={
                  user ? <VisaBlueprint /> : <Navigate to="/login" state={{ from: "/visa-blueprint" }} />
                }
              />
              <Route
                path="/personalized_plan"
                element={
                  user ? <VisaPrepPlanPage /> : <Navigate to="/login" state={{ from: "/personalized_plan" }} />
                }
              />
              <Route
                path="/redflag-indicator"
                element={
                  user ? <RedflagIdentifier /> : <Navigate to="/login" state={{ from: "/redflag-indicator" }} />
                }
              />
              <Route
                path="/VisaConsular_AI"
                element={
                  user ? <Visaconsularchat /> : <Navigate to="/login" state={{ from: "/VisaConsular_AI" }} />
                }
              />
              <Route
                path="/visa-discovery_tool"
                element={
                  user ? <VisaDiscoveryTool /> : <Navigate to="/login" state={{ from: "/visa-discovery_tool" }} />
                }
              />
            </Routes>

          </Suspense>
          <ToastContainer position="bottom-right" />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;